import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, MessageSquareText } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function Proof() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [proof, setProof] = useState(null);
  const [error, setError] = useState(false);
  const [comment, setComment] = useState("");
  const [showChanges, setShowChanges] = useState(params.get("action") === "changes");
  const [submitting, setSubmitting] = useState(false);
  const autoActed = useRef(false);

  useEffect(() => {
    api.get(`/proof/${token}`)
      .then(async (r) => {
        setProof(r.data);
        if (params.get("action") === "approve" && r.data.proof_status === "sent" && !autoActed.current) {
          autoActed.current = true;
          const { data } = await api.post(`/proof/${token}`, { action: "approve" });
          setProof((p) => ({ ...p, proof_status: data.proof_status }));
        }
      })
      .catch(() => setError(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const act = async (action) => {
    if (action === "changes" && !comment.trim()) {
      toast.error("Tell us what you'd like changed.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/proof/${token}`, { action, comment: comment.trim() });
      setProof((p) => ({ ...p, proof_status: data.proof_status, proof_comment: comment.trim() }));
      setShowChanges(false);
    } catch {
      toast.error("Something went wrong — please try again.");
    }
    setSubmitting(false);
  };

  if (error) {
    return (
      <main className="pt-40 pb-32 min-h-screen flex items-center justify-center" data-testid="proof-not-found">
        <p className="font-mono-tech text-sm text-zinc-500 uppercase tracking-widest">Proof not found</p>
      </main>
    );
  }
  if (!proof) return <main className="pt-40 pb-32 min-h-screen" />;

  const approved = proof.proof_status === "approved";
  const changes = proof.proof_status === "changes_requested";

  return (
    <main data-testid="proof-page" className="pt-36 pb-32 min-h-screen">
      <div className="max-w-3xl mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
          <span className="font-mono-tech text-xs uppercase tracking-[0.35em] text-[#C87455]">Proof Approval</span>
          <h1 className="font-serif-display font-light text-4xl md:text-5xl text-white mt-4 tracking-tight">
            {approved ? "Approved — we're on it." : changes ? "Changes requested." : "Take a good look."}
          </h1>
          <p className="text-zinc-400 mt-4 max-w-xl">
            {approved
              ? "Your proof is approved. The piece heads to the laser next — we'll let you know when it ships."
              : changes
              ? "We've received your notes and will send a revised proof shortly."
              : "This is exactly what will be burned or printed. Nothing happens until you approve it."}
          </p>

          <div className="mt-10 border border-zinc-800 bg-[#0C0C0C] p-3">
            <img
              data-testid="proof-image"
              src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${proof.proof_file_id}`}
              alt="Your proof"
              className="w-full object-contain max-h-[560px]"
            />
          </div>

          <div className="mt-4 space-y-1">
            {proof.items.map((it, i) => (
              <p key={i} className="font-mono-tech text-xs text-zinc-500">
                {it.quantity}× {it.name}
                {it.engraving && <span className="text-[#C87455] ml-2">"{it.engraving}"</span>}
              </p>
            ))}
          </div>

          {approved ? (
            <div className="flex items-center gap-3 mt-10 text-emerald-400/90" data-testid="proof-approved-banner">
              <CheckCircle2 size={20} strokeWidth={1.5} />
              <span className="font-mono-tech text-sm uppercase tracking-[0.2em]">Proof approved</span>
            </div>
          ) : changes ? (
            <div className="mt-10 border border-amber-500/30 bg-amber-500/5 p-5" data-testid="proof-changes-banner">
              <p className="font-mono-tech text-xs uppercase tracking-[0.2em] text-amber-400/90 mb-2">Your requested changes</p>
              <p className="text-zinc-300 text-sm italic">"{proof.proof_comment}"</p>
            </div>
          ) : (
            <div className="mt-10">
              {!showChanges ? (
                <div className="flex flex-wrap gap-4">
                  <button
                    data-testid="proof-approve-btn"
                    onClick={() => act("approve")}
                    disabled={submitting}
                    className="bg-[#C87455] text-black hover:bg-[#A65B3E] transition-colors px-10 py-4 font-mono-tech uppercase tracking-[0.2em] text-sm flex items-center gap-3 disabled:opacity-60"
                  >
                    {submitting ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Approve Proof
                  </button>
                  <button
                    data-testid="proof-request-changes-btn"
                    onClick={() => setShowChanges(true)}
                    className="border border-[#A9B0B7]/50 text-[#A9B0B7] hover:bg-[#A9B0B7] hover:text-black transition-colors px-10 py-4 font-mono-tech uppercase tracking-[0.2em] text-sm flex items-center gap-3"
                  >
                    <MessageSquareText size={16} /> Request Changes
                  </button>
                </div>
              ) : (
                <div className="space-y-4" data-testid="proof-changes-form">
                  <label className="font-mono-tech text-xs uppercase tracking-[0.2em] text-zinc-500 block">
                    What should we change?
                  </label>
                  <textarea
                    data-testid="proof-comment-input"
                    value={comment}
                    onChange={(e) => setComment(e.target.value.slice(0, 600))}
                    rows={3}
                    placeholder="Move the text lower, use a bolder font, make the logo bigger…"
                    className="w-full bg-transparent border-b border-zinc-800 focus:border-[#C87455] transition-colors rounded-none px-0 py-3 text-white placeholder-zinc-600 text-sm resize-none"
                  />
                  <div className="flex gap-4">
                    <button
                      data-testid="proof-submit-changes-btn"
                      onClick={() => act("changes")}
                      disabled={submitting}
                      className="bg-[#C87455] text-black hover:bg-[#A65B3E] transition-colors px-8 py-3 font-mono-tech uppercase tracking-[0.2em] text-sm disabled:opacity-60"
                    >
                      {submitting ? "Sending…" : "Send Change Request"}
                    </button>
                    <button onClick={() => setShowChanges(false)} className="font-mono-tech text-xs uppercase tracking-[0.2em] text-zinc-500 hover:text-white transition-colors">
                      Back
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </motion.div>
      </div>
    </main>
  );
}
